import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import type { LDClient } from 'launchdarkly-js-client-sdk'

import { authGuard } from '@/auth'
import { BYOC_BASE_ON_CLOUD_ENVIRONMENT_FLAG } from '@/auth/ld/ld'

export const multipleLevelPages = [
  // these pages are multiple level pages: cluster or tenant or namespace level
  'FunctionsPage',
  'AgentsPage',
  'ConnectorsPage',
  'PulsarConnectorsPage',
  'KafkaConnectorsPage',
  'PulsarConnectorCreatePage',
  'KafkaConnectorCreatePage',
  'ConsumerGroupListPage',
  'ConsumerGroupPage',
  'SchemaListPage',
  'LakehouseTableOverviewPage',
  'LakehouseTableConfigurationPage',
  'LakehouseTableForNativeKafkaOverview',
  'CreateCatalogPage'
]

export const orgLevelPages = [
  'DashboardPage',
  'InstancesPage',
  'UnilinksPage',
  'AlertListPage',
  'MaintenanceNoticeListPage',
  'MaintenanceNoticeDetailPage'
]
export const instanceLevelPages = [
  'ClustersPage',
  'KafkaClustersPage',
  'WorkspacesPage',
  'InstanceOverviewPage',
  'InstanceConfigurationPage'
]
export const workspaceLevelPages = [
  'WorkspaceOverviewPage',
  'WorkspaceAgentsPage',
  'WorkspaceAgentDeployCliPage',
  'WorkspaceFunctionsPage',
  'WorkspaceFunctionDeployCliPage',
  'WorkspaceFunctionPage',
  'WorkspaceFunctionDashboard',
  'WorkspaceFunctionOverview',
  'WorkspaceFunctionLogs',
  'WorkspaceFunctionExceptions',
  'WorkspaceConnectorsPage',
  'WorkspacePulsarConnectorsPage',
  'WorkspaceKafkaConnectorsPage',
  'WorkspaceKafkaConnectorPage',
  'WorkspaceKafkaConnectorDashboard',
  'WorkspaceKafkaConnectorLogs',
  'WorkspaceKafkaConnectorConfigurationPage',
  'WorkspaceKafkaConnectorCreatePage',
  'WorkspaceKafkaConnectorEditPage',
  'WorkspacePulsarConnectorCreatePage',
  'WorkspacePulsarConnectorEditPage',
  'WorkspaceConnectorPage',
  'WorkspaceConnectorDashboard',
  'WorkspaceConnectorLogs',
  'WorkspaceConnectorExceptions',
  'WorkspaceAgentPage',
  'WorkspaceAgentDashboard',
  'WorkspaceAgentOverview',
  'WorkspaceAgentLogs',
  'WorkspaceAgentExceptions',
  'WorkspaceAgentTools',
  'WorkspaceSettingsPage',
  'WorkspaceSettingsGeneralPage',
  'WorkspaceSettingsConnectionsPage',
  'WorkspaceAddConnectionPage'
]
export const clusterLevelPages = [
  'ClusterPage',
  'ClusterDashboard',
  'ClusterOverview',
  'ClusterStorageOverview',
  'ClusterAuditLog',
  'MetricsApiPage',
  'TenantsPage',
  'PulsarClientsPage',
  'KafkaClientsPage',
  'MqttClientsPage',
  'ClusterConfiguration',
  'KafkaClusterPage',
  'KafkaClusterDashboard',
  'KafkaClusterOverview',
  'KafkaClusterConfiguration',
  'LakehouseTableForNativeKafkaOverview',
  'KafkaTopicStats',
  'KafkaTopicConfiguration',
  'KafkaTopicMessages',
  'KafkaTopicPage',
  'KafkaTopicSchema',
  'ClusterMcpConfigPage',
  'KafkaClusterConfiguration',
  ...multipleLevelPages
]
export const tenantLevelPages = [
  'TenantDashboard',
  'NamespacesPage',
  'TenantConfiguration',
  ...multipleLevelPages
]
export const namespaceLevelPages = [
  'TopicMetrics',
  'TopicOverview',
  'TopicSchema',
  'TopicConfiguration',
  'TopicLakehouseTable',
  'TopicLakehouseTableConfiguration',
  'TopicMessages',
  'TopicClients',
  'TopicsPage',
  'KafkaTopicsPage',
  'KafkaTopicMetrics',
  'KafkaTopicSchema',
  'KafkaTopicConfiguration',
  'KafkaTopicMessages',
  'KafkaTopicClients',
  'NamespaceConfiguration',
  'NamespaceOverview',
  'NamespaceDashboard',
  'PfSqlListPage',
  'PfSqlPage',
  'PfSqlOverview',
  'PfSqlLogs',
  'PfSqlExceptions',
  'PfSqlDeployPage',
  'FunctionDashboard',
  'FunctionOverview',
  'FunctionLogs',
  'FunctionExceptions',
  'FunctionPage',
  'AgentDashboard',
  'AgentOverview',
  'AgentLogs',
  'AgentExceptions',
  'AgentTools',
  'AgentPage',
  'PulsarConnectorPage',
  'PulsarConnectorDashboard',
  'PulsarConnectorLogs',
  'PulsarConnectorExceptions',
  'PulsarConnectorEditPage',
  'KafkaConnectorPage',
  'KafkaConnectorLogs',
  'KafkaConnectorDashboard',
  'KafkaConnectorConfigurationPage',
  'KafkaConnectorExceptions',
  'KafkaConnectorEditPage',
  'SubjectSchemaRegistry',
  'KafkaSubjectSchemaRegistry',
  ...multipleLevelPages
]
export const topicLevelPages = [
  'TopicMetrics',
  'TopicOverview',
  'TopicSchema',
  'TopicMessages',
  'TopicClients',
  'TopicStorage',
  'TopicPolicies',
  'TopicStats',
  'TopicConfiguration',
  'TopicLakehouseTable',
  'TopicLakehouseTableConfiguration'
]

export const settingLevelPage = [
  'OrganizationProfilePage',
  'OrganizationUsagePage',
  'WorkspaceUsagePage',
  'UsersPage',
  'ServiceAccountsPage',
  'ServiceAccountPage',
  'RbacPage',
  'CatalogsPage',
  'CatalogDetailPage',
  'CatalogOverviewPage',
  'CreateCatalogPage',
  'SecretsPage',
  'McpServerPage',
  'WorkspaceMcpConfigPage',
  'PreviewFeaturesPage',
  'CreditsPage',
  'AlertListPage',
  'MaintenanceNoticeListPage',
  'MaintenanceNoticeDetailPage'
]

const {
  canDescribeBilling,
  canDescribeCluster,
  canDescribeKafkaCluster,
  canDescribeUser,
  canDescribeCatalogList,
  canCreateCatalog,
  canDescribeInstanceList,
  canDescribeClusterList,
  canDescribeKafkaClusterList,
  canDescribePulsarMetrics,
  canDescribeTenantList,
  canDescribeTenant,
  canDescribeNamespaceList,
  canDescribeNamespace,
  canDescribeTopicList,
  canDescribeTopic,
  canDescribeKafkaTopicList,
  canDescribeKafkaTopic,
  canDescribeSA,
  canDescribeApiKey,
  canDescribeSecret,
  canDescribeConnectorList,
  canDescribeConnector,
  canCreateConnector,
  canUpdateConnector,
  canDescribeFunctionList,
  canDescribePfSqlList,
  canUpdateCluster,
  canDescribeFunction,
  canDescribeCloudEnvironment,
  canDescribeCloudConnection,
  canCreateCloudEnvironment,
  canCreateInstance,
  canUpdateInstance,
  canCreateCluster,
  canProduceMessages,
  canConsumeMessages,
  canConsumeKafkaMessages,
  canDescribeKsnConnectorList,
  canDescribeKsnConnector,
  canCreateKsnConnector,
  canUpdateKsnConnector,
  canDescribePfSql,
  canCreatePfSql,
  canDescribeUnilinkList,
  canDescribeUnilink,
  canCreateUnilink,
  canUpdateUnilink,
  canDescribeRoleBinding,
  canDescribeAlertList,
  canDescribeSchemaSubjects
} = rbacManager()

const openRoutes = <Array<RouteRecordRaw>>[
  // TODO remove me?
  {
    path: '/logs',
    name: 'LogsPage',
    component: () => import('@/components/nb-extension/nb-logs/NbLogs.vue')
  },
  {
    path: '/check-email',
    name: 'CheckEmailPage',
    component: () => import('@/views/login/CheckEmailPage.vue')
  },
  // TODO remove me?
  {
    path: '/email-verified',
    name: 'EmailVerifiedPage',
    component: () => import('@/views/login/EmailVerifiedPage.vue')
  },
  {
    path: '/callback/failed',
    name: 'CallbackFailedPage',
    component: () => import('@/views/login/CallbackFailedPage.vue')
  },
  {
    path: '/callback',
    name: 'CallbackPage',
    component: () => import('@/views/login/CallbackPage.vue')
  },
  // TODO remove me?
  {
    path: '/error',
    name: 'errorPage',
    component: () => import('@/views/error/ErrorPage.vue')
  },
  {
    path: '/404',
    name: 'NotFoundPage',
    component: () => import('@/views/error/NotFoundPage.vue')
  },
  {
    path: '/signup',
    name: 'SignUp',
    component: () => import('@/views/sign-up/SignUp.vue')
  }
].map(r => {
  return {
    ...r,
    meta: { requiresAuth: false }
  }
})

export const authRoutes = <Array<RouteRecordRaw>>[
  {
    path: '/suger/error',
    name: 'SugerErrorPage',
    component: () => import('@/views/login/SugerErrorPage.vue'),
    meta: {
      requiresAuth: true,
      authFn: canDescribeBilling
    }
  },
  {
    path: '/payment',
    name: 'PaymentPage',
    component: () => import('@/views/billing/PaymentPage.vue'),
    meta: {
      requiresAuth: true,
      authFn: canDescribeBilling
    }
  },
  {
    path: '/organizations',
    name: 'OrganizationsPage',
    meta: {
      requiresAuth: true
      // Organizations page is allowed for all users that are authenticated
      // it requires no extra permissions
    },
    component: () => import('@/views/organization/OrganizationsPage.vue')
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      {
        path: '/',
        name: 'DashboardPage',
        component: () => import('@/views/dashboard/DashboardPage.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: '/organization-profile',
        name: 'OrganizationProfilePage',
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        },
        component: () => import('@/views/organization/OrganizationProfilePage.vue')
      },
      {
        path: '/organization-usage',
        name: 'OrganizationUsagePage',
        meta: {
          requiresAuth: true,
          authFn: canDescribeBilling,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        },
        component: () => import('@/views/organization/OrganizationUsagePage.vue')
      },
      {
        path: '/workspace-usage',
        name: 'WorkspaceUsagePage',
        meta: {
          requiresAuth: true,
          authFn: canDescribeBilling,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            if (!to.query.org) {
              return false
            }
            const { workspaceCustomerID } = useOrganization()
            // if metronome customer ID is missing, nothing to show
            return !!workspaceCustomerID.value
          }
        },
        component: () => import('@/views/organization/WorkspaceUsagePage.vue')
      },
      {
        path: '/credits',
        name: 'CreditsPage',
        meta: {
          requiresAuth: true,
          authFn: canDescribeBilling,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        },
        component: () => import('@/views/organization/CreditsPage.vue')
      },
      {
        path: '/users',
        name: 'UsersPage',
        component: () => import('@/views/user/UsersPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeUser,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/alerts',
        name: 'AlertListPage',
        component: () => import('@/views/alert/AlertListPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeAlertList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/maintenance-notices',
        name: 'MaintenanceNoticeListPage',
        component: () => import('@/views/maintenance/MaintenanceNoticeListPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeAlertList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/maintenance-notices/:noticeId',
        name: 'MaintenanceNoticeDetailPage',
        component: () => import('@/views/maintenance/MaintenanceNoticeDetailPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeAlertList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.params.noticeId
          }
        }
      },
      {
        path: '/instances',
        name: 'InstancesPage',
        component: () => import('@/views/instance/InstancesPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeInstanceList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/instance/overview',
        name: 'InstanceOverviewPage',
        component: () => import('@/views/instance/InstanceOverviewPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeInstanceList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance
          }
        }
      },
      {
        path: '/instance/configuration',
        name: 'InstanceConfigurationPage',
        component: () => import('@/views/instance/InstanceConfigurationPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeInstanceList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance
          }
        }
      },
      {
        path: '/catalogs',
        name: 'CatalogsPage',
        component: () => import('@/views/catalog/CatalogsPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCatalogList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/catalogs/create',
        name: 'CreateCatalogPage',
        component: () => import('@/views/catalog/CreateCatalogPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canCreateCatalog,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/catalogs/detail/:name',
        name: 'CatalogDetailPage',
        component: () => import('@/views/catalog/CatalogDetailPage.vue'),
        props: true,
        redirect: { name: 'CatalogOverviewPage' },
        meta: {
          requiresAuth: true,
          authFn: ({ to }: { to: RouteLocationNormalized }) => {
            const { canDescribeCatalog } = rbacManager()
            return canDescribeCatalog({ to })
          },
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        },
        children: [
          {
            path: 'overview',
            name: 'CatalogOverviewPage',
            props: true,
            component: () => import('@/views/catalog/CatalogOverviewPage.vue'),
            meta: {
              requiresAuth: true,
              authFn: ({ to }: { to: RouteLocationNormalized }) => {
                const { canDescribeCatalog } = rbacManager()
                return canDescribeCatalog({ to })
              },
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org
              }
            }
          }
        ]
      },
      {
        path: '/settings/mcp',
        name: 'McpServerPage',
        component: () => import('@/views/mcp/McpServerPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/settings/mcp/cluster',
        name: 'ClusterMcpConfigPage',
        component: () => import('@/views/mcp/ClusterMcpConfigPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.cluster
          },
          disableFn: async ({ to }: { to: RouteLocationNormalized }) => {
            const org = to.query.org as string | undefined

            if (!org) {
              return true
            }

            const { fetchFeatures, isFeatureEnabled } = useFeature()
            await fetchFeatures(org)

            return !isFeatureEnabled('remote-mcp-server')
          },
          replaceFn: () => 'McpServerPage'
        }
      },
      {
        path: '/settings/mcp/workspace',
        name: 'WorkspaceMcpConfigPage',
        component: () => import('@/views/mcp/WorkspaceMcpConfigPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          },
          disableFn: async ({ to }: { to: RouteLocationNormalized }) => {
            const org = to.query.org as string | undefined

            if (!org) {
              return true
            }

            const { fetchFeatures, isFeatureEnabled } = useFeature()
            const { isWorkspacesEnabled } = useOrganization()
            await fetchFeatures(org)

            return !isFeatureEnabled('remote-mcp-server') || !isWorkspacesEnabled.value
          },
          replaceFn: () => 'McpServerPage'
        }
      },
      {
        path: '/settings/previews',
        name: 'PreviewFeaturesPage',
        component: () => import('@/views/feature/PreviewFeaturesPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/clients/pulsar',
        name: 'PulsarClientsPage',
        component: () => import('@/views/client/PulsarClientsPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/clients/kafka',
        name: 'KafkaClientsPage',
        component: () => import('@/views/client/KafkaClientsPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/clients/mqtt',
        name: 'MqttClientsPage',
        component: () => import('@/views/client/MqttClientsPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/metrics-api',
        name: 'MetricsApiPage',
        component: () => import('@/views/client/MetricsApiPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/audit-log',
        name: 'ClusterAuditLog',
        component: () => import('@/views/cluster/AuditLogPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/clusters',
        name: 'ClustersPage',
        component: () => import('@/views/cluster/ClustersPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeClusterList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance
          }
        }
      },
      {
        path: '/kafka-clusters',
        name: 'KafkaClustersPage',
        component: () => import('@/views/cluster/KafkaClustersPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeKafkaClusterList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance
          }
        }
      },
      {
        path: '/workspaces',
        name: 'WorkspacesPage',
        component: () => import('@/views/workspace/WorkspacesPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance
          }
        }
      },
      {
        path: '/workspace',
        name: 'WorkspaceOverviewPage',
        component: () => import('@/views/workspace/WorkspaceOverviewPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/agents',
        name: 'WorkspaceAgentsPage',
        component: () => import('@/views/workspace/agent/WorkspaceAgentsPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/agent/deploy-cli',
        name: 'WorkspaceAgentDeployCliPage',
        component: () => import('@/views/workspace/agent/WorkspaceAgentDeployCliPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/agent/:name',
        component: () => import('@/views/workspace/agent/WorkspaceAgentPage.vue'),
        name: 'WorkspaceAgentPage',
        props: true,
        redirect: { name: 'WorkspaceAgentOverview' },
        children: [
          {
            path: '',
            name: 'WorkspaceAgentOverview',
            component: () => import('@/views/workspace/agent/WorkspaceAgentOverview.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'dashboard',
            name: 'WorkspaceAgentDashboard',
            component: () => import('@/views/workspace/agent/WorkspaceAgentDashboard.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'logs',
            name: 'WorkspaceAgentLogs',
            component: () => import('@/views/workspace/agent/WorkspaceAgentLogs.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'exceptions',
            name: 'WorkspaceAgentExceptions',
            component: () => import('@/views/workspace/agent/WorkspaceAgentExceptions.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'tools',
            name: 'WorkspaceAgentTools',
            component: () => import('@/views/workspace/agent/WorkspaceAgentTools.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/functions',
        name: 'WorkspaceFunctionsPage',
        component: () => import('@/views/workspace/function/WorkspaceFunctionsPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/function/create',
        name: 'WorkspaceFunctionDeployCliPage',
        component: () => import('@/views/workspace/function/WorkspaceFunctionDeployCliPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/connectors',
        name: 'WorkspaceConnectorsPage',
        component: () => import('@/views/connector/ConnectorWrapper.vue'),
        redirect: { name: 'WorkspacePulsarConnectorsPage', params: { type: 'sink' } },
        children: [
          {
            path: 'pulsar/:type',
            name: 'WorkspacePulsarConnectorsPage',
            props: true,
            component: () => import('@/views/connector/PulsarConnectorsPage.vue'),
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'kafka/:type',
            name: 'WorkspaceKafkaConnectorsPage',
            props: true,
            component: () => import('@/views/connector/KafkaConnectorsPage.vue'),
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/connector/:type/:name',
        component: () => import('@/views/connector/pulsar/PulsarConnectorPage.vue'),
        name: 'WorkspaceConnectorPage',
        props: true,
        redirect: { name: 'WorkspaceConnectorDashboard' },
        children: [
          {
            path: 'dashboard',
            name: 'WorkspaceConnectorDashboard',
            component: () => import('@/views/connector/pulsar/PulsarConnectorDashboard.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'logs',
            name: 'WorkspaceConnectorLogs',
            component: () => import('@/views/connector/pulsar/PulsarConnectorLogs.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'exceptions',
            name: 'WorkspaceConnectorExceptions',
            component: () => import('@/views/connector/pulsar/PulsarConnectorExceptions.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/kafka-connector/:type/:name',
        component: () => import('@/views/connector/kafka/KafkaConnectorPage.vue'),
        name: 'WorkspaceKafkaConnectorPage',
        props: true,
        redirect: { name: 'WorkspaceKafkaConnectorDashboard' },
        children: [
          {
            path: 'dashboard',
            name: 'WorkspaceKafkaConnectorDashboard',
            component: () => import('@/views/connector/kafka/KafkaConnectorDashboard.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'logs',
            name: 'WorkspaceKafkaConnectorLogs',
            component: () => import('@/views/connector/kafka/KafkaConnectorLogs.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'configuration',
            name: 'WorkspaceKafkaConnectorConfigurationPage',
            component: () => import('@/views/connector/kafka/KafkaConnectorConfigurationPage.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/function/:name',
        component: () => import('@/views/workspace/function/WorkspaceFunctionPage.vue'),
        name: 'WorkspaceFunctionPage',
        props: true,
        redirect: { name: 'WorkspaceFunctionOverview' },
        children: [
          {
            path: '',
            name: 'WorkspaceFunctionOverview',
            component: () => import('@/views/workspace/function/WorkspaceFunctionOverview.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'dashboard',
            name: 'WorkspaceFunctionDashboard',
            component: () => import('@/views/workspace/function/WorkspaceFunctionDashboard.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'logs',
            name: 'WorkspaceFunctionLogs',
            component: () => import('@/views/workspace/function/WorkspaceFunctionLogs.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'exceptions',
            name: 'WorkspaceFunctionExceptions',
            component: () => import('@/views/workspace/function/WorkspaceFunctionExceptions.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/connector/pulsar/:type/:name/edit',
        name: 'WorkspacePulsarConnectorEditPage',
        props: true,
        component: () => import('@/views/connector/pulsar/PulsarConnectorEditPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/connector/kafka/:type/:name/edit',
        name: 'WorkspaceKafkaConnectorEditPage',
        props: true,
        component: () => import('@/views/connector/kafka/KafkaConnectorEditPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/connector/kafka/:type/create',
        name: 'WorkspaceKafkaConnectorCreatePage',
        props: true,
        component: () => import('@/views/connector/kafka/KafkaConnectorEditPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/connector/pulsar/:type/create',
        name: 'WorkspacePulsarConnectorCreatePage',
        props: true,
        component: () => import('@/views/connector/pulsar/PulsarConnectorEditPage.vue'),
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/workspace/settings',
        component: () => import('@/views/workspace/WorkspaceSettingsPage.vue'),
        name: 'WorkspaceSettingsPage',
        redirect: { name: 'WorkspaceSettingsGeneralPage' },
        children: [
          {
            path: '',
            name: 'WorkspaceSettingsGeneralPage',
            component: () => import('@/views/workspace/WorkspaceSettingsGeneralPage.vue'),
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          },
          {
            path: 'connections',
            name: 'WorkspaceSettingsConnectionsPage',
            component: () => import('@/views/workspace/WorkspaceSettingsConnectionsPage.vue'),
            meta: {
              requiresAuth: true,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.workspace
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.workspace
          }
        }
      },
      {
        path: '/cluster',
        name: 'ClusterPage',
        component: () => import('@/views/cluster/ClusterPage.vue'),
        redirect: { name: 'ClusterDashboard' },
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        },
        children: [
          {
            path: 'dashboard',
            name: 'ClusterDashboard',
            component: () => import('@/views/cluster/ClusterDashboard.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribePulsarMetrics,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.cluster
              },
              replaceFn: () => 'ClusterOverview'
            }
          },
          {
            path: 'overview',
            name: 'ClusterOverview',
            component: () => import('@/views/cluster/ClusterOverview.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeCluster,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.cluster
              }
            }
          },
          {
            path: 'storage',
            name: 'ClusterStorageOverview',
            component: () => import('@/views/cluster/ClusterStorageOverview.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeCluster,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.cluster
              }
            }
          }
        ]
      },
      {
        path: '/kafka-cluster',
        name: 'KafkaClusterPage',
        component: () => import('@/views/cluster/KafkaClusterPage.vue'),
        redirect: { name: 'KafkaClusterDashboard' },
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        },
        children: [
          {
            path: 'dashboard',
            name: 'KafkaClusterDashboard',
            component: () => import('@/views/cluster/KafkaClusterDashboard.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribePulsarMetrics,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.cluster
              },
              replaceFn: () => 'KafkaClusterOverview'
            }
          },
          {
            path: 'overview',
            name: 'KafkaClusterOverview',
            component: () => import('@/views/cluster/KafkaClusterOverview.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeCluster,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.cluster
              }
            }
          }
        ]
      },
      {
        path: '/kafka-cluster-lakehouse-table',
        name: 'LakehouseTableForNativeKafkaOverview',
        component: () => import('@/views/lakehouse-table/LakehouseTableForNativeKafkaOverview.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/cluster/configuration',
        name: 'ClusterConfiguration',
        component: () => import('@/views/cluster/ClusterConfiguration.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/kafka-cluster/configuration',
        name: 'KafkaClusterConfiguration',
        component: () => import('@/views/cluster/KafkaClusterConfiguration.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeKafkaCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/schema-registry',
        name: 'SchemaListPage',
        component: () => import('@/views/schema/SchemaListPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeSchemaSubjects,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/schema-registry/:subject',
        name: 'SubjectSchemaRegistry',
        component: () => import('@/views/topic/schema/KafkaTopicSchema.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          authFn: canDescribeSchemaSubjects,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return (
              !!to.query.org &&
              !!to.query.instance &&
              !!to.query.cluster &&
              !!to.query.tenant &&
              !!to.query.namespace &&
              !!to.query.topic
            )
          }
        }
      },
      {
        path: '/kafka-schema-registry/:subject',
        name: 'KafkaSubjectSchemaRegistry',
        component: () => import('@/views/topic/schema/KafkaTopicSchema.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          authFn: canDescribeSchemaSubjects,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.topic
          }
        }
      },
      {
        path: '/consumer-groups',
        name: 'ConsumerGroupListPage',
        component: () => import('@/views/consumer/ConsumerGroupListPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/consumer-group/:consumerGroupId',
        name: 'ConsumerGroupPage',
        props: true,
        component: () => import('@/views/consumer/ConsumerGroupPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: 'lakehouse-table',
        name: 'LakehouseTableOverviewPage',
        component: () => import('@/views/lakehouse-table/LakehouseTableOverviewPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: 'lakehouse-table/configure',
        name: 'LakehouseTableConfigurationPage',
        component: () => import('@/views/lakehouse-table/LakehouseTableConfigurationPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeCluster,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/tenants',
        name: 'TenantsPage',
        component: () => import('@/views/tenant/TenantsPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeTenantList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/tenant/dashboard',
        name: 'TenantDashboard',
        component: () => import('@/views/tenant/TenantDashboard.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribePulsarMetrics,
          replaceFn: () => 'TenantConfiguration',
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.tenant
          }
        }
      },
      {
        path: 'tenant/configuration',
        name: 'TenantConfiguration',
        component: () => import('@/views/tenant/TenantConfiguration.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeTenant,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.tenant
          }
        }
      },
      {
        path: 'namespaces',
        name: 'NamespacesPage',
        component: () => import('@/views/tenant/NamespaceList.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeNamespaceList,
          replaceFn: () => 'NamespaceOverview',
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.tenant
          }
        }
      },
      {
        path: 'namespace/dashboard',
        name: 'NamespaceDashboard',
        component: () => import('@/views/tenant/namespace/dashboard/NamespaceDashboard.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          authFn: canDescribePulsarMetrics,
          replaceFn: () => 'NamespaceConfiguration',
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return (
              !!to.query.org &&
              !!to.query.instance &&
              !!to.query.cluster &&
              !!to.query.tenant &&
              !!to.query.namespace
            )
          }
        }
      },
      {
        path: 'namespace/overview',
        name: 'NamespaceOverview',
        props: true,
        component: () => import('@/views/tenant/namespace/overview/NamespaceOverview.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeNamespace,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return (
              !!to.query.org &&
              !!to.query.instance &&
              !!to.query.cluster &&
              !!to.query.tenant &&
              !!to.query.namespace
            )
          }
        }
      },
      {
        path: 'namespace/configuration',
        name: 'NamespaceConfiguration',
        props: true,
        component: () =>
          import('@/views/tenant/namespace/configuration/NamespaceConfiguration.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeNamespace,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return (
              !!to.query.org &&
              !!to.query.instance &&
              !!to.query.cluster &&
              !!to.query.tenant &&
              !!to.query.namespace
            )
          }
        }
      },
      {
        path: '/topics',
        component: () => import('@/views/topic/TopicsPage.vue'),
        name: 'TopicsPage',
        meta: {
          requiresAuth: true,
          authFn: canDescribeTopicList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return (
              !!to.query.org &&
              !!to.query.instance &&
              !!to.query.cluster &&
              !!to.query.tenant &&
              !!to.query.namespace
            )
          }
        }
      },
      {
        path: '/kafka-topics',
        component: () => import('@/views/topic/KafkaTopicsPage.vue'),
        name: 'KafkaTopicsPage',
        meta: {
          requiresAuth: true,
          authFn: canDescribeKafkaTopicList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/topic',
        component: () => import('@/views/topic/TopicPage.vue'),
        name: 'TopicPage',
        redirect: { name: 'TopicMetrics' },
        children: [
          {
            path: 'dashboard',
            name: 'TopicMetrics',
            component: () => import('@/views/topic/overview/TopicMetrics.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribePulsarMetrics,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace &&
                  !!to.query.topic
                )
              },
              replaceFn: () => 'TopicOverview'
            }
          },
          {
            path: 'overview',
            name: 'TopicOverview',
            component: () => import('@/views/topic/overview/TopicOverview.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace &&
                  !!to.query.topic
                )
              }
            }
          },
          {
            path: 'schema',
            name: 'TopicSchema',
            component: () => import('@/views/topic/schema/TopicSchema.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace &&
                  !!to.query.topic
                )
              }
            }
          },
          {
            path: 'messages',
            name: 'TopicMessages',
            component: () => import('@/views/topic/messages/TopicMessages.vue'),
            meta: {
              requiresAuth: true,
              authFn: ({ to }: { to: RouteLocationNormalized }) =>
                canConsumeMessages({ to }) || canProduceMessages({ to }),
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace &&
                  !!to.query.topic
                )
              },
              replaceFn: () => 'TopicOverview'
            }
          },
          {
            path: 'clients/:consumerGroupId?',
            name: 'TopicClients',
            component: () => import('@/views/topic/TopicClients.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribeTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace &&
                  !!to.query.topic
                )
              },
              replaceFn: () => 'TopicOverview'
            }
          },
          {
            path: 'configuration',
            name: 'TopicConfiguration',
            component: () => import('@/views/topic/configuration/TopicConfiguration.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace &&
                  !!to.query.topic
                )
              }
            }
          },
          {
            path: 'lakehouse-table',
            name: 'TopicLakehouseTable',
            component: () => import('@/views/topic/lakehouse-table/TopicLakehouseTable.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace &&
                  !!to.query.topic
                )
              }
            }
          },
          {
            path: 'lakehouse-table/configure',
            name: 'TopicLakehouseTableConfiguration',
            component: () =>
              import('@/views/topic/lakehouse-table/TopicLakehouseTableConfiguration.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace &&
                  !!to.query.topic
                )
              }
            }
          },
          {
            path: 'stats',
            name: 'TopicStats',
            component: () => import('@/views/topic/stats/TopicStats.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace &&
                  !!to.query.topic
                )
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          authFn: canDescribeTopicList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return (
              !!to.query.org &&
              !!to.query.instance &&
              !!to.query.cluster &&
              !!to.query.tenant &&
              !!to.query.namespace &&
              !!to.query.topic
            )
          }
        }
      },
      {
        path: '/kafka-topic',
        component: () => import('@/views/topic/KafkaTopicPage.vue'),
        name: 'KafkaTopicPage',
        redirect: (to: RouteLocationNormalized) => {
          if (canDescribePulsarMetrics({ to })) {
            return { name: 'KafkaTopicMetrics', query: to.query }
          }
          return { name: 'KafkaTopicSchema', query: to.query }
        },
        children: [
          {
            path: 'dashboard',
            name: 'KafkaTopicMetrics',
            component: () => import('@/views/topic/overview/KafkaTopicMetrics.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribePulsarMetrics,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.topic
                )
              }
            }
          },
          {
            path: 'schema',
            name: 'KafkaTopicSchema',
            component: () => import('@/views/topic/schema/KafkaTopicSchema.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeKafkaTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.topic
                )
              }
            }
          },
          {
            path: 'messages',
            name: 'KafkaTopicMessages',
            component: () => import('@/views/topic/messages/KafkaTopicMessages.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeKafkaTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.topic
                )
              }
            }
          },
          {
            path: 'clients/:consumerGroupId?',
            name: 'KafkaTopicClients',
            component: () => import('@/views/topic/TopicClients.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribeKafkaTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.topic
                )
              }
            }
          },
          {
            path: 'configuration',
            name: 'KafkaTopicConfiguration',
            component: () => import('@/views/topic/configuration/KafkaTopicConfiguration.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeKafkaTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.topic
                )
              }
            }
          },
          {
            path: 'stats',
            name: 'KafkaTopicStats',
            component: () => import('@/views/topic/stats/TopicStats.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeTopic,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.topic
                )
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          authFn: canDescribeTopicList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.topic
          }
        }
      },
      {
        path: '/service-accounts',
        component: () => import('@/views/service-account/ServiceAccountsPage.vue'),
        name: 'ServiceAccountsPage',
        meta: {
          requiresAuth: true,
          authFn: canDescribeSA,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/service-account/:id',
        name: 'ServiceAccountPage',
        component: () => import('@/views/service-account/ServiceAccountPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeApiKey,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.name
          }
        }
      },
      {
        path: '/rbac/:subject?',
        component: () => import('@/views/rbac/RbacPage.vue'),
        name: 'RbacPage',
        meta: {
          requiresAuth: true,
          authFn: canDescribeRoleBinding,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        },
        props: true
      },
      {
        path: '/secrets',
        component: () => import('@/views/secrets/SecretsPage.vue'),
        name: 'SecretsPage',
        meta: {
          requiresAuth: true,
          authFn: canDescribeSecret,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/connector',
        name: 'ConnectorsPage',
        component: () => import('@/views/connector/ConnectorWrapper.vue'),
        redirect: { name: 'PulsarConnectorsPage', params: { type: 'sink' } },
        children: [
          {
            path: 'pulsar/:type',
            name: 'PulsarConnectorsPage',
            props: true,
            component: () => import('@/views/connector/PulsarConnectorsPage.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeConnectorList,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.cluster
              }
            }
          },
          {
            path: 'kafka/:type',
            name: 'KafkaConnectorsPage',
            props: true,
            component: () => import('@/views/connector/KafkaConnectorsPage.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeKsnConnectorList,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.cluster
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          authFn: ({ to }: { to: RouteLocationNormalized }) =>
            canDescribeConnectorList({ to }) || canDescribeKsnConnectorList({ to }),
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/connector/kafka/:type/:name',
        name: 'KafkaConnectorPage',
        component: () => import('@/views/connector/kafka/KafkaConnectorPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeKsnConnector,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        },
        props: true,
        redirect: { name: 'KafkaConnectorDashboard' },
        children: [
          {
            path: 'dashboard',
            name: 'KafkaConnectorDashboard',
            component: () => import('@/views/connector/kafka/KafkaConnectorDashboard.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribePulsarMetrics,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.cluster
              }
            }
          },
          {
            path: 'logs',
            name: 'KafkaConnectorLogs',
            component: () => import('@/views/connector/kafka/KafkaConnectorLogs.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribeKsnConnector,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.cluster
              }
            }
          },
          {
            path: 'configuration',
            name: 'KafkaConnectorConfigurationPage',
            component: () => import('@/views/connector/kafka/KafkaConnectorConfigurationPage.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribeKsnConnector,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org && !!to.query.instance && !!to.query.cluster
              }
            }
          }
        ]
      },
      {
        path: '/connector/pulsar/:type/:name',
        name: 'PulsarConnectorPage',
        component: () => import('@/views/connector/pulsar/PulsarConnectorPage.vue'),
        redirect: { name: 'PulsarConnectorDashboard' },
        props: true,
        children: [
          {
            path: 'dashboard',
            name: 'PulsarConnectorDashboard',
            component: () => import('@/views/connector/pulsar/PulsarConnectorDashboard.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribePulsarMetrics,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              },
              replaceFn: () => 'PulsarConnectorLogs'
            }
          },
          {
            path: 'logs',
            name: 'PulsarConnectorLogs',
            component: () => import('@/views/connector/pulsar/PulsarConnectorLogs.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribeConnector,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              }
            }
          },
          {
            path: 'exceptions',
            name: 'PulsarConnectorExceptions',
            component: () => import('@/views/connector/pulsar/PulsarConnectorExceptions.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribeConnector,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          authFn: canDescribeConnector,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return (
              !!to.query.org &&
              !!to.query.instance &&
              !!to.query.cluster &&
              !!to.query.tenant &&
              !!to.query.namespace
            )
          }
        }
      },
      {
        path: '/connector/kafka/:type/create',
        name: 'KafkaConnectorCreatePage',
        props: true,
        component: () => import('@/views/connector/kafka/KafkaConnectorEditPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canCreateKsnConnector,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/connector/kafka/:type/:name/edit',
        name: 'KafkaConnectorEditPage',
        component: () => import('@/views/connector/kafka/KafkaConnectorEditPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canUpdateKsnConnector,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        },
        props: true
      },
      {
        path: '/connector/pulsar/:type/create',
        name: 'PulsarConnectorCreatePage',
        props: true,
        component: () => import('@/views/connector/pulsar/PulsarConnectorEditPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canCreateConnector,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/connector/pulsar/:type/:name/edit',
        component: () => import('@/views/connector/pulsar/PulsarConnectorEditPage.vue'),
        name: 'PulsarConnectorEditPage',
        props: true,
        meta: {
          requiresAuth: true,
          authFn: canUpdateConnector,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return (
              !!to.query.org &&
              !!to.query.instance &&
              !!to.query.cluster &&
              !!to.query.tenant &&
              !!to.query.namespace
            )
          }
        }
      },
      {
        path: '/functions',
        name: 'FunctionsPage',
        component: () => import('@/views/function/FunctionsPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeFunctionList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/agents',
        name: 'AgentsPage',
        component: () => import('@/views/agents/AgentsPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribeFunctionList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/pfsqls',
        name: 'PfSqlListPage',
        component: () => import('@/views/pfsql/PfSqlListPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canDescribePfSqlList,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/pfsql/deploy',
        name: 'PfSqlDeployPage',
        component: () => import('@/views/pfsql/PfSqlDeployPage.vue'),
        meta: {
          requiresAuth: true,
          authFn: canCreatePfSql,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster
          }
        }
      },
      {
        path: '/pfsql',
        name: 'PfSqlPage',
        component: () => import('@/views/pfsql/PfSqlPage.vue'),
        redirect: { name: 'PfSqlOverview' },
        children: [
          {
            path: '',
            name: 'PfSqlOverview',
            component: () => import('@/views/pfsql/PfSqlOverview.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribePfSql,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.queryId
                )
              }
            }
          },
          {
            path: 'logs',
            name: 'PfSqlLogs',
            component: () => import('@/views/pfsql/PfSqlLogs.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribePfSql,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.queryId
                )
              }
            }
          },
          {
            path: 'exceptions',
            name: 'PfSqlExceptions',
            component: () => import('@/views/pfsql/PfSqlExceptions.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribePfSql,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.queryId
                )
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          authFn: canDescribePfSql,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org && !!to.query.instance && !!to.query.cluster && !!to.query.queryId
          }
        }
      },
      {
        path: '/function/:name',
        component: () => import('@/views/function/FunctionPage.vue'),
        name: 'FunctionPage',
        props: true,
        redirect: { name: 'FunctionDashboard' },
        children: [
          {
            path: '',
            name: 'FunctionDashboard',
            component: () => import('@/views/function/FunctionDashboard.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribePulsarMetrics,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              },
              replaceFn: () => 'FunctionOverview'
            }
          },
          {
            path: '',
            name: 'FunctionOverview',
            component: () => import('@/views/function/FunctionOverview.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribeFunction,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              }
            }
          },
          {
            path: 'logs',
            name: 'FunctionLogs',
            props: true,
            component: () => import('@/views/function/FunctionLogs.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeFunction,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              }
            }
          },
          {
            path: 'exceptions',
            name: 'FunctionExceptions',
            props: true,
            component: () => import('@/views/function/FunctionExceptions.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeFunction,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          authFn: canDescribeFunction,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return (
              !!to.query.org &&
              !!to.query.instance &&
              !!to.query.cluster &&
              !!to.query.tenant &&
              !!to.query.namespace
            )
          }
        }
      },
      {
        path: '/agent/:name',
        component: () => import('@/views/agents/AgentPage.vue'),
        name: 'AgentPage',
        props: true,
        redirect: { name: 'AgentDashboard' },
        children: [
          {
            path: '',
            name: 'AgentDashboard',
            component: () => import('@/views/agents/AgentDashboard.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribePulsarMetrics,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              },
              replaceFn: () => 'AgentOverview'
            }
          },
          {
            path: '',
            name: 'AgentOverview',
            component: () => import('@/views/agents/AgentOverview.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              authFn: canDescribeFunction,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              }
            }
          },
          {
            path: 'logs',
            name: 'AgentLogs',
            props: true,
            component: () => import('@/views/agents/AgentLogs.vue'),
            meta: {
              requiresAuth: true,
              authFn: canDescribeFunction,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              }
            }
          },
          {
            path: 'tools',
            name: 'AgentTools',
            props: true,
            component: () => import('@/views/agents/AgentTools.vue'), // Placeholder, will create specific component later
            meta: {
              requiresAuth: true,
              authFn: canDescribeFunction,
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return (
                  !!to.query.org &&
                  !!to.query.instance &&
                  !!to.query.cluster &&
                  !!to.query.tenant &&
                  !!to.query.namespace
                )
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          authFn: canDescribeFunction,
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return (
              !!to.query.org &&
              !!to.query.instance &&
              !!to.query.cluster &&
              !!to.query.tenant &&
              !!to.query.namespace
            )
          }
        }
      },
      {
        path: '/environment',
        name: 'CloudEnvironmentManagePage',
        redirect: { name: 'CloudEnvironmentsPage' },
        component: () => import('@/views/environment/CloudEnvironmentManagePage.vue'),
        children: [
          {
            path: 'environments',
            name: 'CloudEnvironmentsPage',
            component: () => import('@/views/environment/CloudEnvironmentsPage.vue'),
            meta: {
              requiresAuth: true,
              authFn: ({ to }: { to: RouteLocationNormalized }) => {
                return canDescribeCloudEnvironment({ to }) && canDescribeCloudConnection({ to })
              },
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org
              },
              disableFn: async ({ ldClient }: { ldClient?: LDClient }) => {
                const flag = await ldClient?.variation(BYOC_BASE_ON_CLOUD_ENVIRONMENT_FLAG, false)
                return !flag
              }
            }
          },
          {
            path: 'connections',
            name: 'CloudConnectionsPage',
            component: () => import('@/views/environment/CloudConnectionsPage.vue'),
            meta: {
              requiresAuth: true,
              authFn: ({ to }: { to: RouteLocationNormalized }) => {
                return canDescribeCloudEnvironment({ to }) && canDescribeCloudConnection({ to })
              },
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org
              },
              disableFn: async ({ ldClient }: { ldClient?: LDClient }) => {
                const flag = await ldClient?.variation(BYOC_BASE_ON_CLOUD_ENVIRONMENT_FLAG, false)
                return !flag
              }
            }
          }
        ],
        meta: {
          requiresAuth: true,
          authFn: ({ to }: { to: RouteLocationNormalized }) => {
            return canDescribeCloudEnvironment({ to }) && canDescribeCloudConnection({ to })
          },
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          },
          disableFn: async ({ ldClient }: { ldClient?: LDClient }) => {
            const flag = await ldClient?.variation(BYOC_BASE_ON_CLOUD_ENVIRONMENT_FLAG, false)
            return !flag
          }
        }
      },
      {
        path: '/unilinks',
        name: 'UnilinksPage',
        component: () => import('@/views/unilink/UnilinksPage.vue'),
        redirect: { name: 'UnilinkDataMigrationsPage' },
        meta: {
          requiresAuth: true,
          authFn: ({ to }: { to: RouteLocationNormalized }) => {
            return canDescribeUnilinkList({ to })
          },
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        },
        children: [
          {
            path: 'data',
            name: 'UnilinkDataMigrationsPage',
            component: () => import('@/views/unilink/UnilinkDataMigrationsPage.vue'),
            meta: {
              requiresAuth: true,
              authFn: ({ to }: { to: RouteLocationNormalized }) => {
                return canDescribeUnilinkList({ to })
              },
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org
              }
            }
          },
          {
            path: 'schema',
            name: 'UnilinkSchemaMigrationsPage',
            component: () => import('@/views/unilink/UnilinkSchemaMigrationsPage.vue'),
            meta: {
              requiresAuth: true,
              authFn: ({ to }: { to: RouteLocationNormalized }) => {
                return canDescribeUnilinkList({ to })
              },
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org
              }
            }
          }
        ]
      },
      {
        path: '/unilinks/detail/schema/:name',
        name: 'UnilinkSchemaOverviewPage',
        component: () => import('@/views/unilink/UnilinkSchemaOverviewPage.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          authFn: ({ to }: { to: RouteLocationNormalized }) => {
            return canDescribeUnilink({ to })
          },
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        }
      },
      {
        path: '/unilinks/detail/data/:name',
        name: 'UnilinkDetailPage',
        component: () => import('@/views/unilink/UnilinkDetailPage.vue'),
        redirect: { name: 'UnilinkOverviewPage' },
        props: true,
        meta: {
          requiresAuth: true,
          authFn: ({ to }: { to: RouteLocationNormalized }) => {
            return canDescribeUnilink({ to })
          },
          checkFn: ({ to }: { to: RouteLocationNormalized }) => {
            return !!to.query.org
          }
        },
        children: [
          {
            path: 'overview',
            name: 'UnilinkOverviewPage',
            props: true,
            component: () => import('@/views/unilink/UnilinkOverviewPage.vue'),
            meta: {
              requiresAuth: true,
              authFn: ({ to }: { to: RouteLocationNormalized }) => {
                return canDescribeUnilink({ to })
              },
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org
              }
            }
          },
          {
            path: 'topics',
            name: 'UnilinkTopicsPage',
            props: true,
            component: () => import('@/views/unilink/UnilinkTopicsPage.vue'),
            meta: {
              requiresAuth: true,
              authFn: ({ to }: { to: RouteLocationNormalized }) => {
                return canDescribeUnilink({ to })
              },
              checkFn: ({ to }: { to: RouteLocationNormalized }) => {
                return !!to.query.org
              }
            }
          }
        ]
      }
    ]
  },
  {
    path: '/deploying',
    name: 'DeployingPage',
    component: () => import('@/views/provision/DeployingPage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      authFn: ({ to }: { to: RouteLocationNormalized }) => {
        return canCreateInstance({ to }) && canCreateCluster({ to })
      },
      checkFn: ({ to }: { to: RouteLocationNormalized }) => {
        return !!to.query.org
      }
    }
  },
  {
    path: '/products',
    name: 'ProductsPage',
    component: () => import('@/views/provision/ProductsPage.vue'),
    meta: {
      requiresAuth: true,
      authFn: canDescribeBilling,
      checkFn: ({ to }: { to: RouteLocationNormalized }) => {
        return !!to.query.org
      }
    }
  },
  {
    path: '/provision',
    name: 'ProvisionPage',
    component: () => import('@/views/provision/ProvisionPage.vue'),
    meta: {
      requiresAuth: true,
      authFn: ({ to }: { to: RouteLocationNormalized }) => {
        return to.query.cluster
          ? canUpdateInstance({ to }) && canUpdateCluster({ to })
          : canCreateInstance({ to }) && canCreateCluster({ to })
      },
      checkFn: ({ to }: { to: RouteLocationNormalized }) => {
        return !!to.query.org
      }
    }
  },
  {
    path: '/environment-provision',
    name: 'CloudEnvironmentProvisionPage',
    component: () => import('@/views/environment/CloudEnvironmentProvisionPage.vue'),
    meta: {
      requiresAuth: true,
      authFn: canCreateCloudEnvironment,
      checkFn: ({ to }: { to: RouteLocationNormalized }) => {
        return !!to.query.org
      },
      disableFn: async ({ ldClient }: { ldClient?: LDClient }) => {
        const flag = await ldClient?.variation(BYOC_BASE_ON_CLOUD_ENVIRONMENT_FLAG, false)
        return !flag
      }
    }
  },
  {
    path: '/workspace/settings/connections/add',
    name: 'WorkspaceAddConnectionPage',
    component: () => import('@/views/workspace/WorkspaceAddConnectionPage.vue'),
    meta: {
      requiresAuth: true,
      checkFn: ({ to }: { to: RouteLocationNormalized }) => {
        return !!to.query.org && !!to.query.instance && !!to.query.workspace
      }
    }
  },
  {
    path: '/unilinks/create/:type',
    name: 'UnilinkCreationPage',
    component: () => import('@/views/unilink/UnilinkCreationPage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      authFn: ({ to }: { to: RouteLocationNormalized }) => {
        return canCreateUnilink({ to })
      },
      checkFn: ({ to }: { to: RouteLocationNormalized }) => {
        return !!to.query.org
      }
    }
  },
  {
    path: '/unilinks/edit/:type/:name',
    name: 'UnilinkEditPage',
    component: () => import('@/views/unilink/UnilinkCreationPage.vue'),
    props: true,
    meta: {
      requiresAuth: true,
      authFn: ({ to }: { to: RouteLocationNormalized }) => {
        return canUpdateUnilink({ to })
      },
      checkFn: ({ to }: { to: RouteLocationNormalized }) => {
        return !!to.query.org
      }
    }
  }
].map(r => {
  // TODO authGuard is also called in beforeEach, is this necessary?
  return {
    ...r,
    beforeEnter: authGuard
  }
})

// These will be conditionally added the to the router for the dev server
// and e2e tests.
export const e2eRoutes = <Array<RouteRecordRaw>>[
  {
    path: '/e2e/tenantadmin',
    name: 'PulsarAdminTest',
    component: () => import('@/views/test/PulsarAdmin.vue'),
    beforeEnter: authGuard,
    meta: {
      requiresAuth: true
    }
  }
]

// this needs to added last and redirect to 404
const notFoundRoutes = <Array<RouteRecordRaw>>[
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

// https://router.vuejs.org/guide/advanced/meta.html
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    authFn?: ({
      to,
      debug
    }: {
      to?: RouteLocationNormalized
      debug?: any
    }) => Promise<boolean> | boolean
    checkFn?: ({
      to,
      debug
    }: {
      to?: RouteLocationNormalized
      debug?: any
    }) => Promise<boolean> | boolean
    disableFn?: ({
      to,
      ldClient,
      debug
    }: {
      to?: RouteLocationNormalized
      ldClient?: LDClient
      debug?: any
    }) => Promise<boolean> | boolean
    replaceFn?: ({
      to,
      ldClient,
      debug
    }: {
      to?: RouteLocationNormalized
      ldClient?: LDClient
      debug?: any
    }) => Promise<RouteLocationNormalized | string> | RouteLocationNormalized | string
  }
}

export default openRoutes.concat(authRoutes).concat(notFoundRoutes)
